import { useState, useRef, useEffect } from "react";
import { MdCloudUpload } from "react-icons/md";
import { AiOutlineClose } from "react-icons/ai";
import { useNavigate } from "react-router-dom";
import ENDPOINTS from "../../api/endpoint";
import Loader from "../Loader";
import { showErrorToast, showSuccessToast } from "../Toast";
import {
  ALLOWED_ACCEPT,
  SUPPORTED_FORMATS_LABEL,
} from "../../constants/allowedFileTypes";

const MAX_FILE_SIZE_MB = 1024; // 1 GB
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

export default function UpdateDocModal({
  isOpen,
  onClose,
  onUpdateSuccess,
  documentId,
  documentName,
  chatbotId = null,
}) {
  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  // Reset selected file when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedFile(null);
    }
  }, [isOpen]);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files?.length > 0) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileInput = (e) => {
    if (e.target.files?.length > 0) {
      handleFile(e.target.files[0]);
    }
  };

  const handleFile = (file) => {
    if (file.size > MAX_FILE_SIZE_BYTES) {
      showErrorToast(`File size must be less than 1 GB`);
      return;
    }
    setSelectedFile(file);
  };

  const openFileDialog = () => {
    fileInputRef.current?.click();
  };

  const removeFile = () => {
    setSelectedFile(null);
  };

  // Update document
  const handleUpdate = async () => {
    if (!documentId) {
      showErrorToast("No document selected to update");
      return;
    }

    if (!selectedFile) {
      showErrorToast("Please select a file to upload");
      return;
    }

    if (selectedFile.size > MAX_FILE_SIZE_BYTES) {
      showErrorToast(`File size must be less than 1 GB`);
      return;
    }

    const user = JSON.parse(localStorage.getItem("user")) || {};
    const token = user.access_token;
    const companyId = user.company_id;
    const userId = user.user_id || user.id;

    if (!companyId || !token) {
      showErrorToast("Missing company or authentication details");
      return;
    }

    setLoading(true);
    onClose?.();

    try {
      const formData = new FormData();
      formData.append("file", selectedFile);

      // Add user_id as Form field (required by backend)
      if (userId) {
        formData.append("user_id", String(userId));
      }

      console.log("Updating document ID:", documentId);
      console.log("New file:", selectedFile.name);

      const headers = {
        Authorization: `Bearer ${token}`,
        "X-User-Id": userId,
      };

      const res = await fetch(
        ENDPOINTS.UPDATE_DOCUMENT(companyId, documentId, chatbotId || undefined),
        {
          method: "PUT",
          headers,
          body: formData,
        }
      );

      const data = await res.json();

      if (!res.ok) {
        showErrorToast(data.detail || "Update failed");
        return;
      }

      // Show detailed success message based on response
      if (data.fallback_to_full) {
        showSuccessToast(
          `Document fully re-processed (${data.total_chunks} chunks)`
        );
      } else if (data.changed_pages) {
        showSuccessToast(
          `Document updated: ${data.changed_pages.length} pages changed, ${data.updated_chunks} chunks updated`
        );
      } else {
        showSuccessToast("Document updated successfully");
      }

      console.log("Update response:", data);

      // Clear selected file after successful update
      setSelectedFile(null);

      // Call the success callback if provided (to refresh parent component)
      if (onUpdateSuccess) {
        onUpdateSuccess();
      }

      navigate("/doc");
    } catch (error) {
      console.error("Update error:", error);
      showErrorToast(error.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Modal */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={onClose}
        >
          <div
            className="bg-white dark:bg-bg-dark rounded-2xl max-w-3xl w-full mx-4 relative p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className={`rounded-xl p-10 text-center transition-colors border-2 border-dashed ${dragActive
                  ? "border-indigo-500 bg-indigo-50 dark:bg-indigo-900"
                  : "border-gray-300 "
                }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              {/* Show document name if provided */}
              {documentName && (
                <div className="mb-6 p-4 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg">
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                    Updating document:
                  </p>
                  <p className="text-lg font-semibold text-gray-900 dark:text-white">
                    {documentName}
                  </p>
                </div>
              )}

              {!selectedFile ? (
                <>
                  <div className="mb-6">
                    <MdCloudUpload className="w-20 h-20 text-indigo-900 dark:text-white mx-auto" />
                  </div>
                  <h3 className="text-lg md:text-3xl font-semibold text-gray-900 dark:text-white mb-2">
                    Choose a file to update or drag & drop it here
                  </h3>
                  <p className="text-base md:text-xl text-gray-500 dark:text-gray-400 mb-6">
                    <span className="font-medium">
                      {SUPPORTED_FORMATS_LABEL}
                    </span>{" "}
                    (max size: 1GB)
                  </p>

                  <button
                    onClick={openFileDialog}
                    className="bg-indigo-900 dark:bg-white hover:bg-indigo-700 text-white dark:text-indigo-900 dark:hover:text-white dark:hover:bg-indigo-900 font-medium py-3 px-8 rounded-xl transition-colors"
                  >
                    Browse Files
                  </button>
                </>
              ) : (
                <div className="text-left">
                  <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    Selected File for Update
                  </h4>
                  <div className="p-4 bg-gray-100 dark:bg-bg-dark rounded-lg flex justify-between items-center">
                    <div className="flex-1 truncate">
                      <span className="truncate dark:text-text-light block font-medium">
                        {selectedFile.name}
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                      </span>
                    </div>
                    <button
                      onClick={removeFile}
                      className="ml-3 text-gray-500 dark:text-text-light hover:text-red-600"
                    >
                      <AiOutlineClose size={18} />
                    </button>
                  </div>
                  <div className="space-x-5 mt-4">
                    <button
                      onClick={openFileDialog}
                      className="bg-gray-500 hover:bg-gray-600 text-white font-medium py-2 px-6 rounded-lg transition-colors"
                    >
                      Choose Different File
                    </button>
                    <button
                      onClick={handleUpdate}
                      className="bg-indigo-900 dark:bg-white hover:bg-indigo-700 text-white dark:text-indigo-900 dark:hover:text-white dark:hover:bg-indigo-900 font-medium py-2 px-6 rounded-lg transition-colors"
                    >
                      Update Document
                    </button>
                  </div>
                </div>
              )}

              <input
                ref={fileInputRef}
                type="file"
                accept={ALLOWED_ACCEPT}
                onChange={handleFileInput}
                className="hidden"
              />
            </div>
          </div>
        </div>
      )}

      {/* Fullscreen Loader */}
      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-[9999]">
          <Loader />
        </div>
      )}
    </>
  );
}
